import { useState, useEffect, Suspense } from "react";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import { IVehicle } from "@/types/vehicle.type";
import { useCompanyStore } from "@/common/states/store/Company";
import { getCompanyId } from "@/common/services/company.api";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import { getLicensingReport } from "@/common/services/vehicles.api";
import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { useNavigate } from "react-router-dom";

const LICENSING_STATUS_OPTIONS = {
  with_issues: "Com Pendências",
  no_issues: "Sem Pendências",
};

const headers = {
  companyId: "Cidade",
  partnerInfoCompanyName: "Parceiro",
  licensePlate: "Placa",
  model: "Modelo",
  lastLicensing: "Último licenciamento",
  missingLicensing: "Status",
  cnpj: "CNPJ",
};

function LicensingReport() {
  const company = useCompanyStore();
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const [vehicles, setVehicles] = useState<
    (IVehicle & { lastLicensing: string; missingLicensing: string })[]
  >([]);

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "updatedAt",
  });

  async function loadLicensingReport(
    currentFilters: TableFiltersState,
    page: number
  ) {
    try {
      setIsLoading(true);

      const result = await getLicensingReport({
        ...currentFilters,
        page: page,
        itemsPerPage: Number(currentFilters.itemsPerPage) || data.itemsPerPage,
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        setVehicles([]);
        return;
      }
      setVehicles(result.data);
    } catch (error) {
      notifyError("Ocorreu um erro inesperado");
      setVehicles([]);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadLicensingReport(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, notifyError, companyId, company]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Relatório de Licenciamento"
          placeholder="Digite a placa do veículo"
          statusOptions={LICENSING_STATUS_OPTIONS}
          onSearch={handleFilterChange}
          entity="vehicle-licensing"
          tableData={vehicles as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={headers}
          data={vehicles as unknown as Record<string, unknown>[]}
          renders={
            {
              companyId: (companyId) => {
                return getCompanySigla(companyId);
              },
              partnerInfoCompanyName: (_value, data) => (
                <span style={{ fontSize: "15px" }}>
                  {data.partnerInfo?.companyName || "N/A"}
                </span>
              ),
              licensePlate: (licensePlate, vehicle) => (
                <a
                  className="text-blue-600 hover:text-bluePrimary cursor-pointer"
                  onClick={() =>
                    navigate(`/veiculos/${(vehicle as IVehicle).licensePlate}`)
                  }
                  target="_blank"
                >
                  {licensePlate}
                </a>
              ),
              model: (value) => (
                <span style={{ fontSize: "15px" }}>{value}</span>
              ),
              lastLicensing: (value) => {
                return <span style={{ fontSize: "15px" }}>{value}</span>;
              },
              missingLicensing: (value) => {
                return (
                  <span
                    className={
                      value === "Com Pendências" ? "text-redPrimary" : ""
                    }
                    style={{ fontSize: "15px", fontWeight: "bold" }}
                  >
                    {value}
                  </span>
                );
              },
            } as unknown as Record<
              string,
              (value: unknown, data: unknown) => JSX.Element
            >
          }
          entity="vehicleLicensing"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}

export default LicensingReport;
